import type { LoginCredentials, RegisterData, User } from '~/types'
import { navigateToLogin } from '~/utils/auth-routing'
import { unsubscribeCurrentPush } from '~/utils/webPush'

export function useAuth() {
  const authStore = useAuthStore()
  const toast = useToast()
  const { post, get, api } = useApi()

  const user = computed(() => authStore.user)
  const isAuthenticated = computed(() => authStore.isAuthenticated)
  const isAdmin = computed(() => authStore.isAdmin)

  async function login(credentials: LoginCredentials) {
    const response = await post<{ user: User }>('/auth/login', credentials)
    if (response.success && response.data?.user) {
      authStore.setAuth(response.data.user)
      toast.add({ title: 'Autentificare reușită', color: 'success' })
      return true
    }
    throw new Error(response.message || 'Autentificare eșuată')
  }

  async function register(data: RegisterData) {
    const response = await post<{ user: User }>('/auth/register', data)
    if (response.success && response.data?.user) {
      authStore.setAuth(response.data.user)
      toast.add({ title: 'Cont creat cu succes', color: 'success' })
      return true
    }
    throw new Error(response.message || 'Înregistrare eșuată')
  }

  async function logout() {
    try {
      await unsubscribeCurrentPush(async (endpoint) => {
        await api.delete('/push/subscribe', { data: { endpoint } })
      })
    } catch {
      // still log out if push unsubscribe fails
    }
    try {
      await post('/auth/logout', {})
    } catch {
      // ignore logout errors
    }
    authStore.clearAuth()
    toast.add({ title: 'Deconectat cu succes', color: 'neutral' })
    await navigateToLogin()
  }

  async function fetchUser() {
    try {
      const response = await get<User | null>('/auth/me')
      if (response.success && response.data) {
        authStore.setUser(response.data)
        return response.data
      }
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status
      if (status === 401 && authStore.user) {
        return authStore.user
      }
    }

    // Access cookie is 15 min; refresh cookie is 7 days. /me with no access
    // token looks logged-out unless we probe refresh (hint cookie or in-memory session).
    if (import.meta.client && (authStore.hasSession || authStore.user)) {
      const refreshed = await authStore.refreshTokens()
      if (refreshed && authStore.user) return authStore.user
    }

    authStore.clearAuth()
    return null
  }

  async function forgotPassword(email: string) {
    const response = await post('/auth/forgot-password', { email })
    if (response.success) {
      toast.add({ title: 'Email trimis', description: 'Verificați inbox-ul pentru instrucțiuni.', color: 'success' })
      return true
    }
    throw new Error(response.message || 'Eroare la trimiterea emailului')
  }

  async function resetPassword(token: string, password: string) {
    const response = await post('/auth/reset-password', { token, password })
    if (response.success) {
      toast.add({ title: 'Parolă resetată cu succes', color: 'success' })
      return true
    }
    throw new Error(response.message || 'Eroare la resetarea parolei')
  }

  async function updateProfile(data: Partial<User>) {
    const response = await post<User>('/auth/profile', data)
    if (response.success && response.data) {
      authStore.setUser(response.data)
      toast.add({ title: 'Profil actualizat', color: 'success' })
      return response.data
    }
    throw new Error(response.message || 'Eroare la actualizarea profilului')
  }

  async function changePassword(currentPassword: string, newPassword: string) {
    const response = await post('/auth/change-password', { currentPassword, newPassword })
    if (response.success) {
      toast.add({ title: 'Parolă schimbată cu succes', color: 'success' })
      return true
    }
    throw new Error(response.message || 'Eroare la schimbarea parolei')
  }

  return {
    user,
    isAuthenticated,
    isAdmin,
    login,
    register,
    logout,
    fetchUser,
    forgotPassword,
    resetPassword,
    updateProfile,
    changePassword,
  }
}
